import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { ArrowCounterClockwise, Robot, User } from '@phosphor-icons/react';
import { fmtStamp } from '@/utils/dateFmt';
import { DateField } from '@/components/ui/date-field';

const API = process.env.REACT_APP_BACKEND_URL;

const RECORD_TYPES = [
  { value: '', label: 'Todos os tipos' },
  { value: 'consulta', label: 'Consulta' },
  { value: 'marcacao', label: 'Marcação' },
  { value: 'meta', label: 'Meta' },
  { value: 'pagamento', label: 'Pagamento' },
  { value: 'cheque', label: 'Cheque Nutricionista' },
  { value: 'cheque_revertido', label: 'Cheque Nutricionista (fatura/pagamento revertido)' },
  { value: 'registo_clinico', label: 'Registo clínico' },
  { value: 'tarefa', label: 'Tarefa' },
  { value: 'plano', label: 'Plano alimentar' },
  { value: 'ementa', label: 'Ementa (semana)' },
  { value: 'receita_cliente', label: 'Receita da cliente' },
  { value: 'ficheiro_paciente', label: 'Ficheiro da paciente' },
  { value: 'fatura', label: 'Fatura' },
  { value: 'alimento_biblioteca', label: 'Alimento da biblioteca' },
  { value: 'alimento_fundido', label: 'Alimento da biblioteca (fundido com outro)' },
  { value: 'paciente_completo', label: 'Paciente (eliminação completa)' },
];

function fmtDate(iso) {
  if (!iso) return 'Sem data';
  return fmtStamp(iso);
}

export default function ApagadosRecentementePage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ record_type: '', patient_id: '', date_from: '', date_to: '', origin: '' });
  const [recoveringId, setRecoveringId] = useState(null);
  const fetchSeq = useRef(0);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const seq = ++fetchSeq.current;
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await axios.get(`${API}/api/admin/deleted-records`, { params });
      if (seq !== fetchSeq.current) return;
      setItems(Array.isArray(data) ? data : []);
    } catch {
      if (seq === fetchSeq.current) toast.error('Erro ao carregar os registos apagados');
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const recover = async (entry, children) => {
    const lines = [`Vais repor "${entry.record_label}"${entry.patient_name ? ` de ${entry.patient_name}` : ''}, apagado em ${fmtDate(entry.deleted_at)}, exatamente como estava.`];
    if (children && children.length > 0) {
      lines.push(`Isto também repõe: ${children.map(c => c.record_label).join(', ')}.`);
    }
    const ok = await confirmDelete({
      description: lines.join(' '),
      confirmLabel: 'Recuperar',
    });
    if (!ok) return;
    setRecoveringId(entry.id);
    try {
      const { data } = await axios.post(`${API}/api/admin/deleted-records/${entry.id}/recover`);
      const linkedResults = data.linked_results || [];
      const failed = linkedResults.filter(r => !r.ok);
      if (failed.length > 0) {
        toast.warning(`Recuperado, mas não foi possível repor: ${failed.map(f => f.record_label).join(', ')} (já existe um registo com o mesmo id).`);
      } else {
        toast.success('Registo recuperado com sucesso' + (linkedResults.length > 0 ? ' (incluindo os registos ligados)' : ''));
      }
      fetchAll();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error(e.response?.data?.detail || 'Já existe um registo com este id. Não foi substituído nada, verifica manualmente.');
      } else {
        toast.error(e.response?.data?.detail || 'Erro ao recuperar o registo');
      }
    } finally {
      setRecoveringId(null);
    }
  };

  const origins = items.filter(i => !i.source_deletion_id);
  const childrenOf = (id) => items.filter(i => i.source_deletion_id === id);
  const standaloneChildren = items.filter(i => i.source_deletion_id && !items.some(o => o.id === i.source_deletion_id));
  const topLevel = [...origins, ...standaloneChildren];

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6" data-testid="apagados-recentemente-page">
        <div>
          <h1 className="text-2xl font-heading text-brand-text">Apagados recentemente</h1>
          <p className="text-sm text-brand-muted mt-1">Histórico de eliminações dos últimos 30 dias (equipa e processos automáticos). Podes repor qualquer registo exatamente como estava.</p>
        </div>

        <div className="bg-white rounded-2xl border border-brand-border p-4 flex flex-wrap gap-3 items-end" data-testid="apagados-filters">
          <div>
            <label className="text-xs text-brand-muted mb-1 block">Tipo</label>
            <select value={filters.record_type} onChange={e => setFilters({ ...filters, record_type: e.target.value })} className="border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="filter-record-type">
              {RECORD_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-brand-muted mb-1 block">Origem</label>
            <select value={filters.origin} onChange={e => setFilters({ ...filters, origin: e.target.value })} className="border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="filter-origin">
              <option value="">Todas as origens</option>
              <option value="user">Feito pela equipa</option>
              <option value="auto">Processo automático</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-brand-muted mb-1 block">De</label>
            <DateField value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} className="border border-brand-border rounded-xl px-3 py-2 text-sm" data-testid="filter-date-from" />
          </div>
          <div>
            <label className="text-xs text-brand-muted mb-1 block">Até</label>
            <DateField value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} className="border border-brand-border rounded-xl px-3 py-2 text-sm" data-testid="filter-date-to" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-xs text-brand-muted mb-1 block">ID da paciente (opcional)</label>
            <input value={filters.patient_id} onChange={e => setFilters({ ...filters, patient_id: e.target.value })} placeholder="user_id" className="border border-brand-border rounded-xl px-3 py-2 text-sm w-full" data-testid="filter-patient-id" />
          </div>
        </div>

        {loading && <p className="text-sm text-brand-muted">A carregar…</p>}

        {!loading && (
          <div className="bg-white rounded-2xl border border-brand-border overflow-hidden" data-testid="apagados-list">
            {items.length === 0 && <p className="text-sm text-brand-muted p-4">Não há registos apagados nestes filtros.</p>}
            <div className="divide-y divide-brand-border">
              {topLevel.map((entry) => {
                const isAuto = (entry.deleted_by || '').startsWith('auto:');
                const children = childrenOf(entry.id);
                return (
                  <div key={entry.id} className="px-4 py-3" data-testid={`deleted-record-${entry.id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-brand-text">{entry.record_label}</span>
                          {entry.patient_name && <span className="text-xs text-brand-muted">· {entry.patient_name}</span>}
                          <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${isAuto ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-brand-mint/20 text-brand-green border-brand-green/30'}`}>
                            {isAuto ? <Robot size={11} /> : <User size={11} />}
                            {isAuto ? (entry.deleted_by_name || 'Processo automático') : (entry.deleted_by_name || 'Equipa')}
                          </span>
                        </div>
                        <p className="text-xs text-brand-muted mt-0.5">Apagado em {fmtDate(entry.deleted_at)}{entry.record_date ? ` · data do registo: ${String(entry.record_date).slice(0, 10)}` : ''}</p>
                      </div>
                      <button
                        onClick={() => recover(entry, children)}
                        disabled={recoveringId === entry.id}
                        className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium flex items-center gap-1.5 disabled:opacity-60 shrink-0"
                        data-testid={`recover-btn-${entry.id}`}
                      >
                        <ArrowCounterClockwise size={14} />
                        {recoveringId === entry.id ? 'A recuperar…' : 'Recuperar'}
                      </button>
                    </div>
                    {children.length > 0 && (
                      <div className="mt-2 ml-4 pl-3 border-l-2 border-brand-border space-y-2" data-testid={`deleted-record-children-${entry.id}`}>
                        {children.map(child => (
                          <div key={child.id} className="flex items-center justify-between gap-3 flex-wrap" data-testid={`deleted-record-${child.id}`}>
                            <div className="min-w-0">
                              <p className="text-xs text-brand-text">{child.record_label} <span className="text-brand-muted">· apagado em conjunto com {entry.record_label}</span></p>
                            </div>
                            <button
                              onClick={() => recover(child, [])}
                              disabled={recoveringId === child.id}
                              className="border border-brand-green text-brand-green rounded-full px-3 py-1 text-[11px] font-medium flex items-center gap-1 disabled:opacity-60 shrink-0"
                              data-testid={`recover-btn-${child.id}`}
                            >
                              <ArrowCounterClockwise size={12} />
                              {recoveringId === child.id ? 'A recuperar…' : 'Recuperar sozinho'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
  );
}
